import { useState } from "react";
import { AlertTriangle, ArrowRightLeft, CheckCircle2, Combine, Info, Link2, Loader2, RotateCcw, Send, Split } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { formatApiError } from "@/lib/api-error";
import { useToast } from "@/hooks/use-toast";
import { useEnviarOrdensServico, useReagruparOrdensServico } from "@/hooks/use-finance-propostas";
import { coresDaSituacaoOs } from "@/components/finance/situacao-os";
import { CadastroClienteOmieDialog } from "@/components/finance/CadastroClienteOmieDialog";
import { formatCentsBRL, formatPercent, Moeda, parseMoeda, simboloMoeda } from "@/utils/money";
import { propostaFoiAceita } from "@/utils/proposta-status";
import type {
  AgrupamentoOsBody,
  OrdemServicoPeriodo,
  OrdemServicoPrevia,
  OrdensServicoPreviaResponse,
  TotalPorEmpresa,
} from "@/types/finance";

interface EtapaOrdensServicoProps {
  propostaId: string;
  previa: OrdensServicoPreviaResponse | undefined;
  carregando: boolean;
  comErro: boolean;
  onRecarregar: () => void;
}

const COR_DA_EMPRESA: Record<string, string> = {
  corisco: "border-sky-200 bg-sky-50 text-sky-700",
  faisca: "border-violet-200 bg-violet-50 text-violet-700",
  gota_serena: "border-teal-200 bg-teal-50 text-teal-700",
};

function ehDoProfissional(ordem: OrdemServicoPrevia) {
  return ordem.tipo === "comissao_freelancer";
}

function jaEstaNoOmie(ordem: OrdemServicoPrevia) {
  return ordem.envio?.status === "enviada" || ordem.situacao_omie !== null;
}

function semVinculoNoOmie(ordem: OrdemServicoPrevia) {
  return ordem.faltando.some((campo) => campo.endsWith("sem vínculo com o Omie"));
}

/** O cadastro que a OS cobra: o profissional na comissão dele, a agência nas demais. */
function cadastroDaOrdem(ordem: OrdemServicoPrevia, agenciaId: string | null) {
  if (ehDoProfissional(ordem)) return ordem.freelancer_id ? { tipo: "freelancer" as const, id: ordem.freelancer_id } : null;
  return agenciaId ? { tipo: "agencia" as const, id: agenciaId } : null;
}

/** Por que o envio está fechado, ou null quando pode enviar. Espelha os bloqueios de os_dispatch.py. */
function motivoSemEnvio(previa: OrdensServicoPreviaResponse, pendentes: OrdemServicoPrevia[]): string | null {
  if (!previa.envio_habilitado) return "O envio ao Omie está desligado na Plataforma.";
  if (!propostaFoiAceita(previa.status_proposta)) return "Disponível depois que a agência aceitar a proposta.";
  if (previa.codigo_provisorio) return "Gere o PDF da proposta para fixar o código antes de enviar.";
  if (pendentes.length === 0) return "Todas estas OS já estão no Omie.";
  if (pendentes.some((ordem) => ordem.faltando.length > 0)) return "Complete os dados que faltam nas OS marcadas.";
  return null;
}

function resumoPorEmpresa(ordens: OrdemServicoPrevia[]) {
  const contagem = new Map<string, number>();
  for (const ordem of ordens) contagem.set(ordem.empresa_nome, (contagem.get(ordem.empresa_nome) ?? 0) + 1);
  return Array.from(contagem, ([nome, total]) => `${total} na ${nome}`).join(", ");
}

/** Proposta em outra moeda: as OS já vêm calculadas em reais pela cotação fixada nos dados da proposta. */
function AvisoDaMoeda({ previa }: { previa: OrdensServicoPreviaResponse }) {
  const moeda = parseMoeda(previa.moeda);
  if (moeda === Moeda.BRL) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {previa.cotacao_omie != null
        ? `Proposta em ${simboloMoeda(moeda)}. Valores das OS em reais, pela cotação da proposta: R$ ${formatPercent(previa.cotacao_omie)}.`
        : `Proposta em ${simboloMoeda(moeda)}, sem cotação. Preencha a cotação em Dados para calcular as OS em reais.`}
    </p>
  );
}

export function EtapaOrdensServico({ propostaId, previa, carregando, comErro, onRecarregar }: EtapaOrdensServicoProps) {
  const reagrupar = useReagruparOrdensServico(propostaId);
  const enviar = useEnviarOrdensServico(propostaId);
  const { toast } = useToast();
  const [paraConfirmar, setParaConfirmar] = useState<OrdemServicoPrevia[] | null>(null);
  const [paraVincular, setParaVincular] = useState<OrdemServicoPrevia | null>(null);

  if (carregando) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }
  if (comErro || !previa) {
    return <ErrorState title="Não foi possível montar as ordens de serviço" onRetry={onRecarregar} />;
  }

  const daAgencia = previa.itens.filter((ordem) => !ehDoProfissional(ordem));
  const doProfissional = previa.itens.filter(ehDoProfissional);

  const enviarAoOmie = (ordens: OrdemServicoPrevia[]) => {
    enviar.mutate(
      { codigos: ordens.map((ordem) => ordem.codigo_integracao) },
      {
        onSuccess: (resposta) => {
          if (resposta.com_erro > 0) {
            toast({
              variant: "destructive",
              title: `${resposta.enviadas} enviada(s), ${resposta.com_erro} com erro`,
              description: "O motivo aparece em cada OS. Corrija e use Tentar novamente.",
            });
          } else {
            toast({ title: `${resposta.enviadas} OS enviada(s) ao Omie` });
          }
        },
        onError: (erro) => {
          const formatado = formatApiError(erro);
          toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
        },
      },
    );
  };

  const aplicar = (corpo: AgrupamentoOsBody) => {
    reagrupar.mutate(corpo, {
      onError: (erro) => {
        const formatado = formatApiError(erro);
        toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p>
            Conferência do que será lançado no Omie, conforme a empresa escolhida em "Faturar por" e a forma de
            cobrar a comissão do profissional.
          </p>
          <p>
            Depois que a agência aceita a proposta, "Enviar ao Omie" cria as OS no Omie, cada uma na empresa
            indicada. As que já estão lá não são criadas de novo. O PDF da proposta não vai anexado.
            {!propostaFoiAceita(previa.status_proposta) && " Esta proposta ainda não foi aceita."}
          </p>
          <p className="text-xs text-sky-800">
            {previa.envio_habilitado
              ? "Depois de enviada, uma OS só é alterada ou cancelada direto no Omie."
              : "O envio ao Omie está desligado. Até ser ligado, estes dados servem para o financeiro lançar as OS."}
            {previa.codigo_provisorio && " O código final é definido quando o PDF é gerado."}
          </p>
        </div>
      </div>

      <AvisoDaMoeda previa={previa} />
      <Tabs defaultValue="agencias" className="w-full">
        <TabsList>
          <TabsTrigger value="agencias">OSs das agências ({daAgencia.length})</TabsTrigger>
          <TabsTrigger value="profissionais">OSs dos profissionais ({doProfissional.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="agencias" className="space-y-4">
          <Resumo
            ordens={daAgencia}
            totais={previa.totais_por_lado?.agencia ?? []}
            previa={previa}
            enviando={enviar.isPending}
            onEnviar={setParaConfirmar}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={reagrupar.isPending}
              onClick={() => aplicar({ acao: "separar_todas" })}
            >
              <Split className="h-3.5 w-3.5" />
              Separar todas
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={reagrupar.isPending}
              onClick={() => aplicar({ acao: "juntar_todas" })}
            >
              <Combine className="h-3.5 w-3.5" />
              Juntar todas
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              disabled={reagrupar.isPending}
              onClick={() => aplicar({ acao: "sugerido" })}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Voltar ao agrupamento sugerido
            </Button>
            {reagrupar.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-xs text-muted-foreground">
            Só períodos faturados pela mesma empresa e com a mesma forma de cobrança podem ficar na mesma OS.
          </p>
          <div className="space-y-3">
            {daAgencia.map((ordem) => (
              <OrdemServicoCard
                key={ordem.codigo_integracao}
                ordem={ordem}
                competencia={previa.competencia}
                destinos={daAgencia.filter(
                  (outra) =>
                    outra.os_id !== ordem.os_id && outra.empresa === ordem.empresa && outra.modo === ordem.modo,
                )}
                movendo={reagrupar.isPending || enviar.isPending}
                onMover={(periodo, destino) =>
                  aplicar({ acao: "mover", item_id: periodo.item_id, os_id: destino?.os_id ?? null })
                }
                enviando={enviar.isPending}
                onTentarDeNovo={(ordem) => enviarAoOmie([ordem])}
                onResolverVinculo={cadastroDaOrdem(ordem, previa.agencia_id) ? setParaVincular : undefined}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="profissionais" className="space-y-4">
          <Resumo
            ordens={doProfissional}
            totais={previa.totais_por_lado?.freelancer ?? []}
            previa={previa}
            enviando={enviar.isPending}
            onEnviar={setParaConfirmar}
          />
          <p className="text-xs text-muted-foreground">
            Uma OS por profissional em cada período. Comissão isenta ou retida na OS da agência não gera OS aqui.
          </p>
          <div className="space-y-3">
            {doProfissional.map((ordem) => (
              <OrdemServicoCard
                key={ordem.codigo_integracao}
                ordem={ordem}
                competencia={previa.competencia}
                enviando={enviar.isPending}
                onTentarDeNovo={(alvo) => enviarAoOmie([alvo])}
                onResolverVinculo={cadastroDaOrdem(ordem, previa.agencia_id) ? setParaVincular : undefined}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={paraConfirmar !== null} onOpenChange={(aberto) => !aberto && setParaConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar {paraConfirmar?.length ?? 0} OS ao Omie?</AlertDialogTitle>
            <AlertDialogDescription>
              Serão criadas {resumoPorEmpresa(paraConfirmar ?? [])}, com{" "}
              {formatCentsBRL((paraConfirmar ?? []).reduce((soma, ordem) => soma + (ordem.valor_cents ?? 0), 0))} no
              total. Depois de enviada, uma OS só é alterada ou cancelada direto no Omie.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (paraConfirmar) enviarAoOmie(paraConfirmar);
                setParaConfirmar(null);
              }}
            >
              Enviar ao Omie
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {paraVincular && cadastroDaOrdem(paraVincular, previa.agencia_id) && (
        <CadastroClienteOmieDialog
          open
          onOpenChange={(aberto) => !aberto && setParaVincular(null)}
          tipo={cadastroDaOrdem(paraVincular, previa.agencia_id)!.tipo}
          cadastroId={cadastroDaOrdem(paraVincular, previa.agencia_id)!.id}
          nome={paraVincular.destinatario.nome}
          empresa={paraVincular.empresa}
          empresaNome={paraVincular.empresa_nome}
          cadastroHabilitado={previa.cadastro_cliente_habilitado}
        />
      )}
    </div>
  );
}

interface ResumoProps {
  ordens: OrdemServicoPrevia[];
  totais: TotalPorEmpresa[];
  previa: OrdensServicoPreviaResponse;
  enviando: boolean;
  onEnviar: (ordens: OrdemServicoPrevia[]) => void;
}

function Resumo({ ordens, totais, previa, enviando, onEnviar }: ResumoProps) {
  const comPendencia = ordens.filter((ordem) => ordem.faltando.length > 0).length;
  const pendentes = ordens.filter((ordem) => !jaEstaNoOmie(ordem));
  const noOmie = ordens.length - pendentes.length;
  const motivo = motivoSemEnvio(previa, pendentes);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {totais.map((total) => (
        <Card key={total.chave}>
          <CardContent className="space-y-1 p-4">
            <p className="text-xs text-muted-foreground">{total.nome}</p>
            <p className="text-xl font-semibold tabular-nums">{formatCentsBRL(total.total_cents)}</p>
          </CardContent>
        </Card>
      ))}
      <Card>
        <CardContent className="flex h-full flex-col justify-center gap-2 p-4">
          <p className="text-xs text-muted-foreground">
            {ordens.length} OS
            {noOmie > 0 && ` · ${noOmie} no Omie`}
            {comPendencia > 0 && ` · ${comPendencia} com dados faltando`}
          </p>
          <Button
            size="sm"
            disabled={motivo !== null || enviando}
            className="gap-1.5"
            onClick={() => onEnviar(pendentes)}
          >
            {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {pendentes.length > 0 && pendentes.length < ordens.length
              ? `Enviar ${pendentes.length} ao Omie`
              : "Enviar ao Omie"}
          </Button>
          {motivo && <p className="text-[11px] text-muted-foreground">{motivo}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

interface OrdemServicoCardProps {
  ordem: OrdemServicoPrevia;
  competencia: string | null;
  destinos?: OrdemServicoPrevia[];
  movendo?: boolean;
  onMover?: (periodo: OrdemServicoPeriodo, destino: OrdemServicoPrevia | null) => void;
  enviando: boolean;
  onTentarDeNovo: (ordem: OrdemServicoPrevia) => void;
  onResolverVinculo?: (ordem: OrdemServicoPrevia) => void;
}

/** O Omie devolve o número com zeros à esquerda (000000000000123). */
function numeroDaOs(numero: string | null | undefined): string | null {
  return numero ? numero.replace(/^0+(?=\d)/, "") : null;
}

function rotuloNoOmie(ordem: OrdemServicoPrevia): string {
  if (ordem.situacao_omie) return ordem.situacao_omie.rotulo;
  if (ordem.envio?.status === "enviada") return "Enviada";
  if (ordem.envio?.status === "enviando") return "Envio não confirmado";
  if (ordem.envio?.status === "erro") return "Erro no envio";
  return "Ainda não lançada";
}

function OrdemServicoCard({
  ordem,
  competencia,
  destinos,
  movendo,
  onMover,
  enviando,
  onTentarDeNovo,
  onResolverVinculo,
}: OrdemServicoCardProps) {
  const situacao = ordem.situacao_omie;
  const coresDaSituacao = situacao ? coresDaSituacaoOs(situacao.chave) : null;
  const envio = ordem.envio;
  const podeTentarDeNovo = !jaEstaNoOmie(ordem) && (envio?.status === "erro" || envio?.status === "enviando");
  const detalhes: Array<[string, string]> = [
    ["Período", ordem.periodo],
    ["Previsão de faturamento", ordem.data_faturamento ?? "-"],
    [
      ehDoProfissional(ordem) ? "Previsão da taxa do profissional" : "Previsão de recebimento",
      ordem.data_recebimento ?? "-",
    ],
    ["Competência", competencia ?? "-"],
    ["Situação no Omie", rotuloNoOmie(ordem)],
    ["Número no Omie", numeroDaOs(envio?.numero_os) ?? "-"],
    ["Código de integração", ordem.codigo_integracao],
  ];

  return (
    <Card
      className={cn(
        "overflow-hidden",
        coresDaSituacao?.fundo,
        ordem.faltando.length > 0 && "border-amber-300",
      )}
    >
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <Badge variant="outline" className={cn("uppercase tracking-wide", COR_DA_EMPRESA[ordem.empresa])}>
          {ordem.empresa_nome}
        </Badge>
        {ordem.ordem !== null && (
          <span className="text-xs font-medium text-muted-foreground">OS {String(ordem.ordem).padStart(2, "0")}</span>
        )}
        {situacao && coresDaSituacao && (
          <Badge variant="outline" className={cn("text-[11px]", coresDaSituacao.selo)}>
            {situacao.rotulo}
          </Badge>
        )}
        {!situacao && envio?.status === "enviada" && (
          <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-[11px] text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            No Omie{envio.numero_os && ` · nº ${numeroDaOs(envio.numero_os)}`}
          </Badge>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{ordem.destinatario.nome}</p>
          <p className="truncate text-xs text-muted-foreground">
            {ordem.cargo} · {ordem.profissional}
          </p>
        </div>
        <p className="text-lg font-semibold tabular-nums">{formatCentsBRL(ordem.valor_cents)}</p>
      </div>
      {podeTentarDeNovo && (
        <div className="flex flex-wrap items-center gap-2 border-b bg-red-50 px-4 py-2 text-xs text-red-800">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            {envio?.status === "erro"
              ? `O Omie recusou esta OS: ${envio.erro ?? "sem detalhe"}`
              : "O envio desta OS foi interrompido. Tentar de novo confere no Omie antes de criar."}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            disabled={enviando || ordem.faltando.length > 0}
            onClick={() => onTentarDeNovo(ordem)}
          >
            <RotateCcw className="h-3 w-3" />
            Tentar novamente
          </Button>
        </div>
      )}
      {envio?.mudou_depois && (
        <div className="flex items-start gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          A proposta mudou depois do envio. Esta OS ficou no Omie com os dados antigos: ajuste direto no Omie.
        </div>
      )}
      {ordem.faltando.length > 0 && (
        <div className="flex items-start gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">Falta para lançar: {ordem.faltando.join(", ")}</span>
          {onResolverVinculo && semVinculoNoOmie(ordem) && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 bg-background px-2 text-xs"
              onClick={() => onResolverVinculo(ordem)}
            >
              <Link2 className="h-3 w-3" />
              Resolver vínculo
            </Button>
          )}
        </div>
      )}
      {ordem.avisos.length > 0 && (
        <div className="flex items-start gap-2 border-b bg-sky-50 px-4 py-2 text-xs text-sky-800">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {ordem.avisos.join(" ")}
        </div>
      )}
      {onMover && ordem.itens.length > 0 && (
        <div className="border-b px-4 py-3 text-sm">
          <p className="text-[11px] text-muted-foreground">
            {ordem.itens.length === 1 ? "Período nesta OS" : `${ordem.itens.length} períodos nesta OS`}
          </p>
          <ul className="mt-1 divide-y rounded-md border bg-background">
            {ordem.itens.map((periodo) => (
              <li key={periodo.item_id} className="flex flex-wrap items-center gap-3 px-3 py-1.5">
                <span className="min-w-0 flex-1 truncate">
                  {periodo.profissional} · {periodo.periodo}
                  {periodo.data_faturamento && (
                    <span className="text-muted-foreground"> · fatura em {periodo.data_faturamento}</span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums font-medium">{formatCentsBRL(periodo.valor_cents)}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                      disabled={movendo || ((destinos?.length ?? 0) === 0 && ordem.itens.length === 1)}
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                      Mover para
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="text-xs">Mover este período para</DropdownMenuLabel>
                    {(destinos ?? []).map((destino) => (
                      <DropdownMenuItem key={destino.codigo_integracao} onSelect={() => onMover(periodo, destino)}>
                        OS {String(destino.ordem ?? 0).padStart(2, "0")} · {destino.profissional}
                      </DropdownMenuItem>
                    ))}
                    {ordem.itens.length > 1 && (
                      <>
                        {(destinos?.length ?? 0) > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuItem onSelect={() => onMover(periodo, null)}>Nova OS</DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        </div>
      )}
      <dl className="grid border-b bg-muted/20 text-sm sm:grid-cols-3">
        {detalhes.map(([rotulo, valor]) => (
          <div key={rotulo} className="border-b px-4 py-2 last:border-b-0 sm:border-r sm:nth-[3n]:border-r-0">
            <dt className="text-[11px] text-muted-foreground">{rotulo}</dt>
            <dd className="font-medium">{valor}</dd>
          </div>
        ))}
      </dl>
      <div className="space-y-3 px-4 py-3 text-sm">
        {ordem.linhas.length > 1 ? (
          <div>
            <p className="text-[11px] text-muted-foreground">Serviços da OS</p>
            <ul className="mt-1 divide-y rounded-md border bg-background">
              {ordem.linhas.map((linha, indice) => (
                <li key={`${indice}-${linha.descricao}`} className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="min-w-0 truncate">{linha.descricao}</span>
                  <span className="shrink-0 tabular-nums font-medium">{formatCentsBRL(linha.valor_cents)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div>
            <p className="text-[11px] text-muted-foreground">Descrição do serviço</p>
            <p className="whitespace-pre-line">{ordem.descricao}</p>
          </div>
        )}
        <div>
          <p className="text-[11px] text-muted-foreground">Observação</p>
          <p className="whitespace-pre-line text-muted-foreground">{ordem.observacao}</p>
        </div>
        <details className="text-xs">
          <summary className="cursor-pointer font-medium text-muted-foreground">Ver dados técnicos do lançamento</summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded bg-muted p-3 text-[11px] leading-relaxed">
            {JSON.stringify(ordem.payload, null, 2)}
          </pre>
        </details>
      </div>
    </Card>
  );
}
